//-- copyright
// OpenProject Resource Planner (multi-resource) management plugin.
//++

import {
  ChangeDetectorRef, Component, ElementRef, Input, OnInit, inject,
} from '@angular/core';
import {
  ResourcePlannerService, RpData, RpResource, RpTag, RpCalendar, RpRateRow, RpAvailability,
} from './resource-planner.service';
import {
  WeekDay, WorkWeek, CalException, Recurrence, RecurRange, DayType, normalizeCalendar,
} from '../shared/timephased.util';

interface TypeDef { key:string; name:string; color:string; hasUnits:boolean; hasRate:boolean; hasUser:boolean; }
interface Member { id:number; name:string; }
interface Row { node:RpResource; depth:number; }

@Component({
  selector: 'rp-resource-management',
  templateUrl: './resource-management.component.html',
  styleUrls: ['./resource-management.component.sass'],
  standalone: false,
})
export class ResourceManagementComponent implements OnInit {
  @Input() projectId = '';
  @Input() projectName = '';

  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  private svc!:ResourcePlannerService;

  readonly TYPES:TypeDef[] = [
    { key: 'work',      name: 'Work',      color: '#0969da', hasUnits: true,  hasRate: true,  hasUser: true  },
    { key: 'equipment', name: 'Equipment', color: '#8250df', hasUnits: true,  hasRate: true,  hasUser: false },
    { key: 'material',  name: 'Material',  color: '#bf3989', hasUnits: false, hasRate: false, hasUser: false },
    { key: 'cost',      name: 'Cost',      color: '#1a7f37', hasUnits: false, hasRate: false, hasUser: false },
  ];
  readonly TAG_COLORS:Record<string,{bg:string;fg:string;dot:string}> = {
    blue:   { bg: '#ddf4ff', fg: '#0a3069', dot: '#0969da' },
    green:  { bg: '#dafbe1', fg: '#0a3622', dot: '#1a7f37' },
    purple: { bg: '#fbefff', fg: '#3e1f7a', dot: '#8250df' },
    orange: { bg: '#fff1e5', fg: '#7d4e00', dot: '#bc4c00' },
    pink:   { bg: '#ffeff7', fg: '#7d2070', dot: '#bf3989' },
    red:    { bg: '#ffebe9', fg: '#82071e', dot: '#cf222e' },
    yellow: { bg: '#fff8c5', fg: '#6b5900', dot: '#9a6700' },
    gray:   { bg: '#eaeef2', fg: '#393f46', dot: '#59636e' },
    teal:   { bg: '#d4f7f4', fg: '#0a4f4a', dot: '#118a82' },
  };
  readonly TAG_COLOR_KEYS = Object.keys(this.TAG_COLORS);
  readonly CURRENCIES = ['EUR', 'USD', 'GBP'];
  readonly DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // Calendar (v2 / MS Project parity) option lists.
  readonly DAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  readonly DAY_TYPES:{ key:DayType; label:string }[] = [
    { key: 'WORKING', label: 'Working' },
    { key: 'NON_WORKING', label: 'Non-working' },
    { key: 'DEFAULT', label: 'Default (inherit)' },
  ];
  readonly CAL_TYPES = ['PROJECT', 'RESOURCE', 'TASK'];
  readonly RECUR_TYPES:{ key:string; label:string }[] = [
    { key: 'NONE', label: 'Does not repeat' },
    { key: 'DAILY', label: 'Daily' },
    { key: 'WEEKLY', label: 'Weekly' },
    { key: 'MONTHLY', label: 'Monthly' },
    { key: 'YEARLY', label: 'Yearly' },
  ];
  readonly ORDINALS = ['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'LAST'];
  readonly RANGE_KINDS:{ key:string; label:string }[] = [
    { key: 'NO_END', label: 'No end date' },
    { key: 'END_DATE', label: 'Until date' },
    { key: 'OCCURRENCES', label: 'After N occurrences' },
  ];
  readonly MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  readonly RATE_TABLE_KEYS = ['A', 'B', 'C', 'D', 'E'];
  readonly RATE_TABLE_NAMES:Record<string,string> = {
    A: 'A — Standard', B: 'B — Overtime', C: 'C — Off-site', D: 'D — Emergency', E: 'E — Internal',
  };
  rateTab = 'A';

  data:RpData = { canManage: false, tags: [], calendars: [], resources: [] };
  members:Member[] = [];
  loading = true;
  error:string|null = null;
  flash:string|null = null;
  tab:'resources'|'calendars'|'tags' = 'resources';

  expanded = new Set<number>();
  search = '';
  typeFilter = 'all';
  tagFilter:number[] = [];      // selected tag ids to filter the resource tree by
  tagFilterOpen = false;         // multi-select dropdown open state

  // resource dialog
  resDialog = false;
  resForm:any = null;
  resTab:'details'|'tags'|'rates'|'availability' = 'details';

  // tag dialog
  tagDialog = false;
  tagForm:any = null;

  // calendar dialog
  calDialog = false;
  calForm:any = null;
  calTab:'week'|'exceptions'|'weeks'|'advanced' = 'week';

  ngOnInit():void {
    if (!this.projectId) {
      this.projectId = this.elementRef.nativeElement.getAttribute('project-id') || '';
    }
    this.svc = new ResourcePlannerService(this.projectId);
    this.reload();
    this.loadMembers();
  }

  get canManage():boolean { return this.data.canManage; }

  reload():void {
    this.loading = true;
    this.svc.data().then((d) => {
      this.data = d;
      // auto-expand all groups on first load
      if (this.expanded.size === 0) {
        this.walk(d.resources, (n) => { if (n.isGroup) { this.expanded.add(n.id); } });
      }
      this.loading = false;
      this.cdr.detectChanges();
    }).catch(() => { this.error = 'Failed to load resources.'; this.loading = false; this.cdr.detectChanges(); });
  }

  private loadMembers():void {
    fetch(`/api/v3/projects/${this.projectId}/available_assignees?pageSize=200`,
      { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((j:any) => {
        this.members = ((j?._embedded?.elements) || []).map((u:any) => ({
          id: this.idFromHref(u?._links?.self?.href), name: u.name,
        })).filter((m:Member) => m.id);
        this.cdr.detectChanges();
      }).catch(() => { /* members optional */ });
  }

  private idFromHref(href?:string):number {
    const m = (href || '').match(/\/(\d+)$/);
    return m ? Number(m[1]) : 0;
  }

  private notify(msg:string):void {
    this.flash = msg;
    setTimeout(() => { this.flash = null; this.cdr.detectChanges(); }, 2600);
  }

  // ── tree helpers ───────────────────────────────────────────────
  private walk(nodes:RpResource[], fn:(n:RpResource)=>void):void {
    nodes.forEach((n) => { fn(n); if (n.children) { this.walk(n.children, fn); } });
  }

  toggle(id:number):void {
    if (this.expanded.has(id)) { this.expanded.delete(id); } else { this.expanded.add(id); }
  }

  // ── delegated row/card actions ──────────────────────────────────
  // Interactive elements inside the *ngFor lists (resource rows, tag rows, calendar
  // cards) carry `data-rp-act` + `data-rp-id` instead of their own (click) binding:
  // a single listener on each list container dispatches them. Per-row (click)
  // bindings were silently inert on the packaged (DEB) deployment — the click
  // reached the button, but the embedded view's listener never ran and nothing was
  // logged. One listener in the parent view is immune to that and is cheaper besides
  // (95 rows × 3 buttons = 285 listeners before).
  onDelegatedClick(ev:Event):void {
    const el = (ev.target as HTMLElement|null)?.closest?.('[data-rp-act]') as HTMLElement|null;
    if (!el) { return; }
    const id = Number(el.getAttribute('data-rp-id'));
    if (!id) { return; }

    switch (el.getAttribute('data-rp-act')) {
      case 'res-toggle': this.toggle(id); break;
      case 'res-add': this.newResource(false, id); break;
      case 'res-edit': { const n = this.resourceById(id); if (n) { this.editResource(n); } break; }
      case 'res-delete': { const n = this.resourceById(id); if (n) { this.deleteResource(n); } break; }
      case 'tag-edit': { const t = this.data.tags.find((x) => x.id === id); if (t) { this.editTag(t); } break; }
      case 'tag-delete': { const t = this.data.tags.find((x) => x.id === id); if (t) { this.deleteTag(t); } break; }
      case 'cal-edit': { const c = this.data.calendars.find((x) => x.id === id); if (c) { this.editCalendar(c); } break; }
      case 'cal-delete': { const c = this.data.calendars.find((x) => x.id === id); if (c) { this.deleteCalendar(c); } break; }
      case 'cal-usage': { const c = this.data.calendars.find((x) => x.id === id); if (c) { this.openCalResources(c); } break; }
      default: return;
    }
    // The handlers above only mutate component state; render it explicitly rather
    // than relying on the ambient change-detection pass.
    this.cdr.detectChanges();
  }

  // Stable identity for the row *ngFor. `rows` is a getter that returns fresh
  // wrapper objects on every read, so without this NgForOf destroys and re-creates
  // all row views on every change-detection pass.
  trackRow = (_i:number, r:Row):number => r.node.id;

  private resourceById(id:number):RpResource|undefined {
    const stack:RpResource[] = [...this.data.resources];
    while (stack.length) {
      const n = stack.shift() as RpResource;
      if (n.id === id) { return n; }
      if (n.children) { stack.push(...n.children); }
    }
    return undefined;
  }
  expandAll():void { this.walk(this.data.resources, (n) => { if (n.isGroup) { this.expanded.add(n.id); } }); }
  collapseAll():void { this.expanded.clear(); }

  // ── tag filter (multi-select) ──────────────────────────────────
  toggleTagFilter(id:number):void {
    const i = this.tagFilter.indexOf(id);
    if (i >= 0) { this.tagFilter.splice(i, 1); } else { this.tagFilter.push(id); }
  }
  clearTagFilter():void { this.tagFilter = []; }

  private matches(n:RpResource):boolean {
    const q = this.search.trim().toLowerCase();
    const okQ = !q || n.name.toLowerCase().includes(q) || (n.initials || '').toLowerCase().includes(q);
    const okT = this.typeFilter === 'all' || (!n.isGroup && n.resourceType === this.typeFilter);
    // tag filter matches a leaf resource carrying ANY of the selected tags;
    // groups never carry tags, so they surface only via a matching descendant
    const okTags = this.tagFilter.length === 0
      || (!n.isGroup && this.tagFilter.some((t) => n.tagIds.includes(t)));
    return okQ && okT && okTags;
  }
  private subtreeMatches(n:RpResource):boolean {
    return this.matches(n) || (n.children || []).some((c) => this.subtreeMatches(c));
  }

  get rows():Row[] {
    const filtering = !!this.search.trim() || this.typeFilter !== 'all' || this.tagFilter.length > 0;
    const out:Row[] = [];
    const build = (nodes:RpResource[], depth:number) => {
      nodes.forEach((n) => {
        if (filtering && !this.subtreeMatches(n)) { return; }
        out.push({ node: n, depth });
        const open = this.expanded.has(n.id) || (filtering && (n.children || []).some((c) => this.subtreeMatches(c)));
        if (n.children && open) { build(n.children, depth + 1); }
      });
    };
    build(this.data.resources, 0);
    return out;
  }

  get leafCount():number { let c = 0; this.walk(this.data.resources, (n) => { if (!n.isGroup) { c++; } }); return c; }
  get groupCount():number { let c = 0; this.walk(this.data.resources, (n) => { if (n.isGroup) { c++; } }); return c; }

  typeDef(key:string|null):TypeDef { return this.TYPES.find((t) => t.key === key) || this.TYPES[0]; }
  calName(id:number|null):string { const c = this.data.calendars.find((x) => x.id === id); return c ? c.name : '—'; }
  tagById(id:number):RpTag|undefined { return this.data.tags.find((t) => t.id === id); }
  tagColor(c:string) { return this.TAG_COLORS[c] || this.TAG_COLORS.gray; }
  money(v:number, cur:string):string {
    const sym:Record<string,string> = { EUR: '€', USD: '$', GBP: '£' };
    return (sym[cur] || '') + Number(v || 0).toLocaleString('en-US');
  }
  rateText(n:RpResource):string {
    const t = this.typeDef(n.resourceType);
    if (t.hasRate && n.rate) { return `${this.money(n.rate, n.currency)}/h`; }
    return '—';
  }

  // group options for parent picker
  get groupOptions():{id:number;name:string;depth:number}[] {
    const acc:{id:number;name:string;depth:number}[] = [];
    const rec = (nodes:RpResource[], depth:number) => {
      nodes.forEach((n) => { if (n.isGroup) { acc.push({ id: n.id, name: n.name, depth }); rec(n.children || [], depth + 1); } });
    };
    rec(this.data.resources, 0);
    return acc;
  }

  // ── resource dialog ────────────────────────────────────────────
  newResource(isGroup:boolean, parentId:number|null = null):void {
    this.error = null;
    this.resTab = 'details';
    const defCal = this.data.calendars.find((c) => c.isDefault);
    this.rateTab = 'A';
    this.resForm = {
      id: null, isGroup, resourceType: 'work', name: '', initials: '',
      parentId, maxUnits: 100, currency: 'EUR', rate: 0,
      calendarId: defCal ? defCal.id : null, userId: null, tagIds: [], unitLabel: '',
      rateTables: this.emptyRateTables(), availabilities: [],
    };
    this.resDialog = true;
  }
  editResource(n:RpResource):void {
    this.error = null;
    this.resTab = 'details';
    this.rateTab = 'A';
    this.resForm = {
      id: n.id, isGroup: n.isGroup, resourceType: n.resourceType || 'work', name: n.name,
      initials: n.initials || '', parentId: this.parentOf(n.id), maxUnits: n.maxUnits,
      currency: n.currency, rate: n.rate,
      calendarId: n.calendarId, userId: n.userId, tagIds: [...n.tagIds], unitLabel: n.unitLabel || '',
      rateTables: this.cloneRateTables(n.rateTables), availabilities: (n.availabilities || []).map((a) => ({ ...a })),
    };
    this.resDialog = true;
  }

  // ── rate tables (A–E, effective-dated) ──────────────────────────
  private emptyRateTables():Record<string,RpRateRow[]> {
    const out:Record<string,RpRateRow[]> = {};
    this.RATE_TABLE_KEYS.forEach((k) => { out[k] = []; });
    return out;
  }
  private cloneRateTables(src:Record<string,RpRateRow[]>|undefined):Record<string,RpRateRow[]> {
    const out = this.emptyRateTables();
    if (src) { this.RATE_TABLE_KEYS.forEach((k) => { out[k] = (src[k] || []).map((r) => ({ ...r })); }); }
    return out;
  }
  rateRows(table:string):RpRateRow[] { return this.resForm?.rateTables?.[table] || []; }
  addRateRow(table:string):void {
    this.resForm.rateTables[table].push({ effectiveDate: null, rate: this.resForm.rate || 0 });
  }
  removeRateRow(table:string, i:number):void { this.resForm.rateTables[table].splice(i, 1); }

  // ── availability ranges ─────────────────────────────────────────
  addAvailability():void {
    this.resForm.availabilities.push({ from: null, to: null, maxUnits: this.resForm.maxUnits || 100 });
  }
  removeAvailability(i:number):void { this.resForm.availabilities.splice(i, 1); }

  private cleanRateTables(src:Record<string,RpRateRow[]>):Record<string,RpRateRow[]> {
    const out:Record<string,RpRateRow[]> = {};
    this.RATE_TABLE_KEYS.forEach((k) => {
      const rows = (src[k] || []).map((r) => ({ effectiveDate: r.effectiveDate || null, rate: +r.rate || 0 }));
      if (rows.length) { out[k] = rows; }
    });
    return out;
  }
  private cleanAvailabilities(src:RpAvailability[]):RpAvailability[] {
    return (src || [])
      .filter((a) => a.from || a.to || a.maxUnits != null)
      .map((a) => ({ from: a.from || null, to: a.to || null, maxUnits: +a.maxUnits || 0 }));
  }
  private parentOf(id:number):number|null {
    let res:number|null = null;
    const rec = (nodes:RpResource[], parent:number|null) => {
      nodes.forEach((n) => { if (n.id === id) { res = parent; } if (n.children) { rec(n.children, n.id); } });
    };
    rec(this.data.resources, null);
    return res;
  }
  autoInitials():void {
    if (!this.resForm) { return; }
    const words = this.resForm.name.trim().split(/\s+/).filter(Boolean);
    const ini = words.length >= 2 ? (words[0][0] + words[1][0]) : (this.resForm.name.slice(0, 2));
    this.resForm.initials = (ini || '').toUpperCase();
  }
  toggleFormTag(id:number):void {
    const arr:number[] = this.resForm.tagIds;
    const i = arr.indexOf(id);
    if (i >= 0) { arr.splice(i, 1); } else { arr.push(id); }
  }
  // tags not yet on the resource (offered in the "Add tags" picker)
  availableTags():RpTag[] { return this.data.tags.filter((t) => !this.resForm.tagIds.includes(t.id)); }
  // switch resource type; drop to Details if the active tab no longer applies
  setResType(key:string):void {
    this.resForm.resourceType = key;
    const td = this.typeDef(key);
    if ((this.resTab === 'rates' && !td.hasRate) || (this.resTab === 'availability' && !td.hasUnits)) {
      this.resTab = 'details';
    }
  }
  // total rate tiers across all A–E tables (tab badge)
  totalRateTiers():number {
    const rt = this.resForm?.rateTables || {};
    return this.RATE_TABLE_KEYS.reduce((s, k) => s + ((rt[k] || []).length), 0);
  }
  saveResource():void {
    const f = this.resForm;
    if (!f.name.trim()) { this.error = 'Name is required.'; return; }
    const payload:Record<string,unknown> = {
      is_group: f.isGroup, name: f.name, initials: f.initials, parent_id: f.parentId,
      resource_type: f.isGroup ? null : f.resourceType, max_units: f.maxUnits, currency: f.currency,
      rate: f.rate, unit_label: f.unitLabel,
      resource_planner_calendar_id: f.calendarId, user_id: f.userId, tag_ids: f.tagIds,
      rate_tables: this.cleanRateTables(f.rateTables), availabilities: this.cleanAvailabilities(f.availabilities),
    };
    const done = (msg:string) => { this.resDialog = false; this.notify(msg); this.reload(); };
    const fail = (e:any) => { this.error = (e?.errors || ['Save failed']).join(', '); this.cdr.detectChanges(); };
    if (f.id) { this.svc.updateResource(f.id, payload).then(() => done(`${f.name} updated`)).catch(fail); }
    else { this.svc.createResource(payload).then(() => done(`${f.name} created`)).catch(fail); }
  }
  deleteResource(n:RpResource):void {
    const msg = n.isGroup ? `Delete group “${n.name}” and all its items?` : `Delete resource “${n.name}”?`;
    if (!window.confirm(msg)) { return; }
    this.svc.deleteResource(n.id).then(() => { this.notify(`${n.name} deleted`); this.reload(); })
      .catch(() => { this.error = 'Delete failed'; this.cdr.detectChanges(); });
  }

  // ── tags ───────────────────────────────────────────────────────
  newTag():void { this.error = null; this.tagForm = { id: null, name: '', color: 'blue', description: '' }; this.tagDialog = true; }
  editTag(t:RpTag):void { this.error = null; this.tagForm = { id: t.id, name: t.name, color: t.color, description: t.description || '' }; this.tagDialog = true; }
  saveTag():void {
    const f = this.tagForm;
    if (!f.name.trim()) { this.error = 'Tag name is required.'; return; }
    const payload = { name: f.name, color: f.color, description: f.description };
    const done = () => { this.tagDialog = false; this.notify('Tag saved'); this.reload(); };
    const fail = (e:any) => { this.error = (e?.errors || ['Save failed']).join(', '); this.cdr.detectChanges(); };
    if (f.id) { this.svc.updateTag(f.id, payload).then(done).catch(fail); }
    else { this.svc.createTag(payload).then(done).catch(fail); }
  }
  deleteTag(t:RpTag):void {
    if (!window.confirm(`Delete tag “${t.name}”?`)) { return; }
    this.svc.deleteTag(t.id).then(() => { this.notify('Tag deleted'); this.reload(); })
      .catch(() => { this.error = 'Delete failed'; this.cdr.detectChanges(); });
  }
  tagUsage(id:number):number { let c = 0; this.walk(this.data.resources, (n) => { if (n.tagIds.includes(id)) { c++; } }); return c; }

  // ── calendars (v2 / MS Project parity) ─────────────────────────────
  private standardWeek():WeekDay[] {
    return this.DAY_NAMES.map((day, i) => (i >= 1 && i <= 5
      ? { day, type: 'WORKING' as DayType, hours: [{ from: '09:00', to: '17:00' }] }
      : { day, type: 'NON_WORKING' as DayType, hours: [] }));
  }
  private cloneWeek(wd:WeekDay[]):WeekDay[] {
    return this.DAY_NAMES.map((day, i) => {
      const src = (wd || []).find((d) => (d.day || '').toUpperCase() === day);
      return {
        day,
        type: (src?.type || (i >= 1 && i <= 5 ? 'WORKING' : 'NON_WORKING')) as DayType,
        hours: (src?.hours || []).map((h) => ({ ...h })),
      };
    });
  }

  newCalendar():void {
    this.error = null;
    this.calTab = 'week';
    this.calForm = {
      id: null, name: '', description: '', isDefault: this.data.calendars.length === 0,
      type: 'PROJECT', parentCalendarId: null,
      minutesPerDay: 480, minutesPerWeek: 2400, daysPerMonth: 20,
      weekDays: this.standardWeek(), workWeeks: [], exceptions: [],
    };
    this.calDialog = true;
  }
  editCalendar(c:RpCalendar):void {
    this.error = null;
    this.calTab = 'week';
    const d = normalizeCalendar(c.definition);
    this.calForm = {
      id: c.id, name: c.name, description: c.description || '', isDefault: c.isDefault,
      type: d.type || 'PROJECT', parentCalendarId: d.parentCalendarId ?? null,
      minutesPerDay: d.minutesPerDay ?? 480,
      minutesPerWeek: d.minutesPerWeek ?? 2400,
      daysPerMonth: d.daysPerMonth ?? 20,
      weekDays: this.cloneWeek(d.weekDays || []),
      workWeeks: (d.workWeeks || []).map((w) => ({
        name: w.name || '', from: w.from, to: w.to, weekDays: this.cloneWeek(w.weekDays || []),
      })),
      exceptions: (d.exceptions || []).map((e) => ({
        name: e.name || '', from: e.from, to: e.to, working: !!e.working,
        hours: (e.hours || []).map((h) => ({ ...h })),
        recurring: e.recurring ? JSON.parse(JSON.stringify(e.recurring)) : null,
      })),
    };
    this.calDialog = true;
  }

  // Other calendars in this project, offered as inheritance parents (excluding
  // the one being edited, to avoid a trivial self-cycle).
  get parentCalendarOptions():RpCalendar[] {
    return this.data.calendars.filter((c) => c.id !== this.calForm?.id);
  }

  // Per-day hour ranges (shared by weekDays and workWeek days).
  addRange(day:WeekDay):void { (day.hours = day.hours || []).push({ from: '09:00', to: '17:00' }); }
  removeRange(day:WeekDay, i:number):void { day.hours.splice(i, 1); }
  dayMinutes(day:WeekDay):number {
    return (day.hours || []).reduce((s, h) => {
      const a = this.toMin(h.from); const b = this.toMin(h.to);
      return (a != null && b != null && b > a) ? s + (b - a) : s;
    }, 0);
  }
  dayHoursLabel(day:WeekDay):string {
    const m = this.dayMinutes(day);
    return m ? `${Math.round((m / 60) * 100) / 100}h` : '';
  }
  private toMin(s:string):number|null {
    const m = /^(\d{1,2}):(\d{2})$/.exec(s || ''); return m ? +m[1] * 60 + +m[2] : null;
  }

  // Work weeks (dated weekly overrides).
  addWorkWeek():void {
    this.calForm.workWeeks.push({ name: '', from: '', to: '', weekDays: this.cloneWeek([]) });
  }
  removeWorkWeek(i:number):void { this.calForm.workWeeks.splice(i, 1); }

  // Exceptions (holidays, catch-up days, recurring closures).
  addException():void {
    this.calForm.exceptions.push({ name: '', from: '', to: '', working: false, hours: [], recurring: null });
  }
  removeException(i:number):void { this.calForm.exceptions.splice(i, 1); }
  onExceptionWorkingChange(ex:CalException):void {
    if (ex.working && !(ex.hours && ex.hours.length)) { ex.hours = [{ from: '09:00', to: '17:00' }]; }
    if (!ex.working) { ex.hours = []; }
  }
  recurType(ex:CalException):string { return ex.recurring ? ex.recurring.type : 'NONE'; }
  onRecurTypeChange(ex:CalException, type:string):void {
    if (type === 'NONE') { ex.recurring = null; return; }
    const range:RecurRange = ex.recurring?.range || { kind: 'NO_END' };
    const base:Recurrence = { type: type as Recurrence['type'], every: 1, range };
    if (type === 'WEEKLY') { base.weekdays = ex.recurring?.weekdays || ['MONDAY']; }
    if (type === 'MONTHLY' || type === 'YEARLY') {
      base.relative = ex.recurring?.relative || false;
      base.ordinal = ex.recurring?.ordinal || 'FIRST';
      base.weekday = ex.recurring?.weekday || 'MONDAY';
      base.day = ex.recurring?.day || 1;
      if (type === 'YEARLY') { base.month = ex.recurring?.month || 1; }
    }
    if (type === 'DAILY') { base.workingDaysOnly = ex.recurring?.workingDaysOnly || false; }
    ex.recurring = base;
  }
  toggleRecurWeekday(ex:CalException, day:string):void {
    if (!ex.recurring) { return; }
    const arr = ex.recurring.weekdays = ex.recurring.weekdays || [];
    const i = arr.indexOf(day);
    if (i >= 0) { arr.splice(i, 1); } else { arr.push(day); }
  }
  onRangeKindChange(rec:Recurrence, kind:string):void {
    rec.range = { kind: kind as RecurRange['kind'] };
    if (kind === 'OCCURRENCES') { rec.range.count = 10; }
    if (kind === 'END_DATE') { rec.range.date = null; }
  }

  saveCalendar():void {
    const f = this.calForm;
    if (!f.name.trim()) { this.error = 'Calendar name is required.'; return; }
    // Send the v2 camelCase definition directly — the backend sanitizer
    // (CalendarDefinition.normalize) validates and canonicalises it.
    const payload = {
      name: f.name, description: f.description, is_default: f.isDefault,
      definition: {
        type: f.type, parentCalendarId: f.parentCalendarId,
        minutesPerDay: +f.minutesPerDay || 480,
        minutesPerWeek: +f.minutesPerWeek || 2400,
        daysPerMonth: +f.daysPerMonth || 20,
        weekDays: f.weekDays,
        workWeeks: (f.workWeeks || []).filter((w:WorkWeek) => w.from && w.to),
        exceptions: (f.exceptions || []).filter((e:CalException) => e.from),
      },
    };
    const done = () => { this.calDialog = false; this.notify('Calendar saved'); this.reload(); };
    const fail = (e:any) => { this.error = (e?.errors || ['Save failed']).join(', '); this.cdr.detectChanges(); };
    if (f.id) { this.svc.updateCalendar(f.id, payload).then(done).catch(fail); }
    else { this.svc.createCalendar(payload).then(done).catch(fail); }
  }
  deleteCalendar(c:RpCalendar):void {
    if (!window.confirm(`Delete calendar “${c.name}”?`)) { return; }
    this.svc.deleteCalendar(c.id).then(() => { this.notify('Calendar deleted'); this.reload(); })
      .catch(() => { this.error = 'Delete failed'; this.cdr.detectChanges(); });
  }
  calUsage(id:number):number { let c = 0; this.walk(this.data.resources, (n) => { if (n.calendarId === id) { c++; } }); return c; }

  // Resource-list modal for a calendar.
  calResModal:RpCalendar|null = null;
  openCalResources(c:RpCalendar):void { this.calResModal = c; }
  closeCalResources():void { this.calResModal = null; }
  // Leaf resources explicitly assigned to this calendar (matches calUsage).
  calResources(id:number):RpResource[] {
    const out:RpResource[] = [];
    this.walk(this.data.resources, (n) => { if (!n.isGroup && n.calendarId === id) { out.push(n); } });
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }

  // ── calendar card summary ──────────────────────────────────────────
  workingDayLabels(def:RpCalendar['definition']|null|undefined):string {
    const wd = normalizeCalendar(def).weekDays || [];
    const labels = this.DAY_NAMES
      .map((name, i) => ({ i, wd: wd.find((d) => (d.day || '').toUpperCase() === name) }))
      .filter((x) => x.wd && x.wd.type !== 'NON_WORKING')
      .map((x) => this.DOW[x.i]);
    return labels.join(' ') || '—';
  }
  calHoursLabel(def:RpCalendar['definition']|null|undefined):string {
    const d = normalizeCalendar(def);
    const working = (d.weekDays || []).filter((w) => w.type === 'WORKING' && (w.hours || []).length);
    if (!working.length) { return '—'; }
    const sig = (w:WeekDay) => (w.hours || []).map((h) => `${h.from}–${h.to}`).join(', ');
    const first = sig(working[0]);
    return working.every((w) => sig(w) === first) ? first : 'varies by day';
  }
  calParentName(def:RpCalendar['definition']|null|undefined):string|null {
    const pid = normalizeCalendar(def).parentCalendarId;
    if (pid == null) { return null; }
    return this.data.calendars.find((c) => c.id === pid)?.name || null;
  }
  calExceptionCount(def:RpCalendar['definition']|null|undefined):number {
    return (normalizeCalendar(def).exceptions || []).length;
  }
}
